import React, { ReactNode, useMemo } from 'react';
import { Column } from '@tanstack/react-table';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  TanstackTableColumnFacet,
  TanstackTableFilterOption,
  TanstackTableFilterVariant,
} from './types';
import { useTanstackTableLocale } from './locale';

/**
 * A `Select` in a labelled `FormControl`, or bare when no label is supplied.
 * `shrink` is forced because these selects use `displayEmpty`.
 */
const FilterSelectControl = ({
  children,
  label,
}: {
  children: ReactNode;
  label?: string;
}): JSX.Element => {
  return (
    <FormControl size="small" fullWidth>
      {label && <InputLabel shrink>{label}</InputLabel>}
      {children}
    </FormControl>
  );
};

interface ColumnFilterProps<TData> {
  column: Column<TData, unknown>;
  facet?: TanstackTableColumnFacet;
}

interface FilterOption {
  label: string;
  value: string | number | boolean;
  count?: number;
}

/** Collects the available values for `select` / `multiSelect` filters. */
function useFilterOptions<TData>(
  column: Column<TData, unknown>,
  facet?: TanstackTableColumnFacet
): FilterOption[] {
  return useMemo(() => {
    const explicit = column.columnDef.meta?.filterOptions;
    if (explicit)
      return explicit.map((o: TanstackTableFilterOption) => ({ label: o.label, value: o.value }));

    if (facet?.uniqueValues) {
      return facet.uniqueValues
        .filter(
          ([value]: [unknown, number]) => value !== undefined && value !== null && value !== ''
        )
        .map(([value, count]: [unknown, number]) => ({
          label: String(value),
          value: value as string,
          count,
        }));
    }

    const faceted = column.getFacetedUniqueValues();
    return Array.from(faceted.entries())
      .filter(([value]: [unknown, number]) => value !== undefined && value !== null && value !== '')
      .map(([value, count]: [unknown, number]) => ({
        label: String(value),
        value: value as string,
        count,
      }))
      .sort((a: FilterOption, b: FilterOption) => a.label.localeCompare(b.label));
  }, [column, facet]);
}

function useMinMax<TData>(
  column: Column<TData, unknown>,
  facet?: TanstackTableColumnFacet
): [number | undefined, number | undefined] {
  return useMemo(() => {
    if (facet && (facet.min !== undefined || facet.max !== undefined)) {
      return [facet.min, facet.max];
    }
    const range = column.getFacetedMinMaxValues();
    return range ? [range[0], range[1]] : [undefined, undefined];
  }, [column, facet]);
}

/**
 * Renders the appropriate filter control for a column based on
 * `meta.filterVariant`. Works identically in client and server mode — in server
 * mode the value still flows through `column.setFilterValue`, which the table's
 * change handlers forward to the consumer.
 */
function ColumnFilter<TData>({ column, facet }: ColumnFilterProps<TData>): JSX.Element {
  const locale = useTanstackTableLocale();
  const variant: TanstackTableFilterVariant = column.columnDef.meta?.filterVariant ?? 'text';
  const value = column.getFilterValue();
  const options = useFilterOptions(column, facet);
  const [facetMin, facetMax] = useMinMax(column, facet);

  if (variant === 'select') {
    return (
      <Box sx={{ minWidth: 200, p: 1 }}>
        <FilterSelectControl label={locale.filterLabel}>
          <Select
            size="small"
            fullWidth
            displayEmpty
            label={locale.filterLabel}
            value={(value as string | number | undefined) ?? ''}
            onChange={(e: SelectChangeEvent<string | number>): void =>
              column.setFilterValue(e.target.value === '' ? undefined : e.target.value)
            }
          >
            <MenuItem value="">
              <em>{locale.filterAll}</em>
            </MenuItem>
            {options.map((option: FilterOption) => (
              <MenuItem key={String(option.value)} value={option.value as string | number}>
                {option.label}
                {option.count !== undefined ? ` (${option.count})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FilterSelectControl>
      </Box>
    );
  }

  if (variant === 'multiSelect') {
    const selected = Array.isArray(value) ? (value as (string | number | boolean)[]) : [];
    const toggle = (optionValue: string | number | boolean): void => {
      const next = selected.includes(optionValue)
        ? selected.filter((v: string | number | boolean) => v !== optionValue)
        : [...selected, optionValue];
      column.setFilterValue(next.length ? next : undefined);
    };
    return (
      <Box sx={{ minWidth: 220, maxHeight: 280, overflowY: 'auto', p: 1 }}>
        {options.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            {locale.noValues}
          </Typography>
        )}
        {options.map((option: FilterOption) => (
          <MenuItem key={String(option.value)} dense onClick={(): void => toggle(option.value)}>
            <Checkbox size="small" checked={selected.includes(option.value)} />
            <ListItemText
              primary={option.label}
              secondary={option.count !== undefined ? `${option.count}` : undefined}
            />
          </MenuItem>
        ))}
      </Box>
    );
  }

  if (variant === 'range' || variant === 'number') {
    const [min, max] = (Array.isArray(value) ? value : [undefined, undefined]) as [
      number | undefined,
      number | undefined
    ];
    if (variant === 'number') {
      return (
        <Box sx={{ minWidth: 200, p: 1 }}>
          <TextField
            size="small"
            type="number"
            fullWidth
            label={locale.equals}
            value={(value as number | string | undefined) ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
              column.setFilterValue(e.target.value === '' ? undefined : Number(e.target.value))
            }
          />
        </Box>
      );
    }
    return (
      <Stack direction="row" spacing={1} sx={{ minWidth: 240, p: 1 }}>
        <TextField
          size="small"
          type="number"
          label={facetMin !== undefined ? `${locale.min} (${facetMin})` : locale.min}
          value={min ?? ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
            column.setFilterValue([e.target.value === '' ? undefined : Number(e.target.value), max])
          }
        />
        <TextField
          size="small"
          type="number"
          label={facetMax !== undefined ? `${locale.max} (${facetMax})` : locale.max}
          value={max ?? ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
            column.setFilterValue([min, e.target.value === '' ? undefined : Number(e.target.value)])
          }
        />
      </Stack>
    );
  }

  if (variant === 'boolean') {
    return (
      <Box sx={{ minWidth: 180, p: 1 }}>
        <FilterSelectControl label={locale.filterLabel}>
          <Select
            size="small"
            fullWidth
            displayEmpty
            label={locale.filterLabel}
            value={value === undefined || value === '' ? '' : String(value)}
            onChange={(e: SelectChangeEvent<string>): void => {
              const raw = e.target.value;
              column.setFilterValue(raw === '' ? undefined : raw === 'true');
            }}
          >
            <MenuItem value="">
              <em>{locale.filterAll}</em>
            </MenuItem>
            <MenuItem value="true">{locale.yes}</MenuItem>
            <MenuItem value="false">{locale.no}</MenuItem>
          </Select>
        </FilterSelectControl>
      </Box>
    );
  }

  if (variant === 'date') {
    const [from, to] = (Array.isArray(value) ? value : [undefined, undefined]) as [
      string | undefined,
      string | undefined
    ];
    return (
      <Stack spacing={1} sx={{ minWidth: 220, p: 1 }}>
        <TextField
          size="small"
          type="date"
          label={locale.from}
          InputLabelProps={{ shrink: true }}
          value={from ?? ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
            column.setFilterValue([e.target.value || undefined, to])
          }
        />
        <TextField
          size="small"
          type="date"
          label={locale.to}
          InputLabelProps={{ shrink: true }}
          value={to ?? ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
            column.setFilterValue([from, e.target.value || undefined])
          }
        />
      </Stack>
    );
  }

  // Default: free-text contains.
  return (
    <Box sx={{ minWidth: 200, p: 1 }}>
      <FormControlLabel
        sx={{ m: 0, width: '100%' }}
        control={
          <TextField
            size="small"
            fullWidth
            autoFocus
            label={locale.filterLabel}
            placeholder={locale.filterPlaceholder}
            value={(value as string | undefined) ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
              column.setFilterValue(e.target.value || undefined)
            }
          />
        }
        label=""
      />
    </Box>
  );
}

export default ColumnFilter;
